// Review 1003: the prefilled application, laid out the way the LOS 1003 tab
// lays it out (review1003Manifest.ts), one borrower at a time, with every
// value a document filled highlighted and tagged with that document. Edit
// opens the LOS 1003 itself in a drawer; clicking a file or a tag opens the
// document.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Spin, message } from "antd";
import { ArrowRightOutlined, EditOutlined, FileTextOutlined, WarningOutlined } from "@ant-design/icons";
import Interview1003ViewV2 from "../Interview1003View/Interview1003ViewV2";
import {
  LODocument,
  LOProvenanceField,
  LOStatus,
  downloadLODocument,
  getLOApplication1003,
  getLOProvenance,
} from "./loOnePagerClient";
import { TranslateLO } from "./loOnePagerI18n";
import {
  FieldItem,
  PartyInfo,
  Role,
  RowItem,
  SECTION_NAV,
  SectionModel,
  SubSection,
  buildReviewModel,
  comparable,
  formatMoney,
  listParties,
  monthlyIncomeOf,
  OPT,
} from "./review1003Manifest";
import { fileIcon } from "./LOStageViews";

// The LOS 1003 is a JavaScript component with many optional props; typed
// loosely here so the drawer can pass only what the review needs.
const Interview1003: any = Interview1003ViewV2;

interface Props {
  loanId: string;
  status: LOStatus;
  t: TranslateLO;
  busy: boolean;
  onConfirm: () => void;
}

type ProvIndex = Map<string, LOProvenanceField>;

const provKey = (role: Role, path: string) => `${role}\u0000${path}`;

// A bracketed path is matched loosely: the bracket's contents and the row's
// identity are both reduced to letters and digits before comparing.
const loose = (s: string) => `${s || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");

const findProv = (index: ProvIndex, looseIndex: Map<string, LOProvenanceField>, role: Role, paths?: string[]) => {
  if (!paths || !paths.length) return null;
  for (const p of paths) {
    const hit = index.get(provKey(role, p));
    if (hit) return hit;
    if (p.indexOf("[") >= 0) {
      const l = looseIndex.get(provKey(role, loose(p)));
      if (l) return l;
    }
  }
  return null;
};

type Mark = { prov: LOProvenanceField; edited: boolean } | null;

const markFor = (index: ProvIndex, looseIndex: Map<string, LOProvenanceField>, role: Role, item: FieldItem): Mark => {
  const prov = findProv(index, looseIndex, role, item.prov);
  if (!prov) return null;
  const current = comparable(item.raw ?? item.value);
  const extracted = comparable(prov.value);
  const edited = !!current && !!extracted && current !== extracted;
  return { prov, edited };
};

const Tag: React.FC<{ mark: Mark; t: TranslateLO; onOpen: (docId: number) => void }> = ({ mark, t, onOpen }) => {
  if (!mark) return null;
  if (mark.edited) {
    return (
      <span className="lop-tag is-edited" title={`${mark.prov.doc_title}: ${mark.prov.value}`}>
        {t("edited")}
      </span>
    );
  }
  return (
    <button type="button" className="lop-tag" title={mark.prov.filename} onClick={() => onOpen(mark.prov.doc_serialid)}>
      <FileTextOutlined rev={undefined} />
      {mark.prov.doc_title}
    </button>
  );
};

const LOReview1003: React.FC<Props> = ({ loanId, status, t, busy, onConfirm }) => {
  const [app, setApp] = useState<any>(null);
  const [prov, setProv] = useState<LOProvenanceField[]>([]);
  const [loading, setLoading] = useState(true);
  const [activePartyId, setActivePartyId] = useState<string>("");
  const [editing, setEditing] = useState<{ anchor: string; title: string } | null>(null);
  const [preview, setPreview] = useState<{ doc: LODocument | null; docId: number } | null>(null);
  const [previewData, setPreviewData] = useState<{ content: string; mime: string; name: string } | null | "loading">(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([getLOApplication1003(loanId), getLOProvenance(loanId)]);
      setApp(a);
      setProv(p);
      setActivePartyId((cur) => cur && a?.participantsById?.[cur] ? cur : a?.primaryPartyId || "");
    } catch (e: any) {
      message.error(e?.message || t("loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [loanId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const parties: PartyInfo[] = useMemo(() => listParties(app), [app]);
  const activeParty = parties.find((p) => p.partyId === activePartyId) || parties[0];
  const model: SectionModel[] = useMemo(() => (activeParty ? buildReviewModel(app, activeParty.partyId) : []), [app, activeParty]);

  const { index, looseIndex } = useMemo(() => {
    const idx: ProvIndex = new Map();
    const lidx = new Map<string, LOProvenanceField>();
    prov.forEach((p) => {
      idx.set(provKey(p.role, p.path), p);
      lidx.set(provKey(p.role, loose(p.path)), p);
    });
    return { index: idx, looseIndex: lidx };
  }, [prov]);

  const docsById = useMemo(() => {
    const m = new Map<number, LODocument>();
    status.documents.forEach((d) => m.set(d.serialid, d));
    return m;
  }, [status.documents]);

  const openDoc = useCallback(
    async (docId: number) => {
      setPreview({ doc: docsById.get(docId) || null, docId });
      setPreviewData("loading");
      try {
        const data = await downloadLODocument(loanId, docId);
        setPreviewData(data);
      } catch (e) {
        setPreviewData(null);
      }
    },
    [docsById, loanId]
  );

  const roleFor = (section: SectionModel): Role => (section.shared ? "" : activeParty?.role || "");

  // How many fields of this section a document filled, for the badge.
  const extractedCount = (section: SectionModel): number => {
    const role = roleFor(section);
    let n = 0;
    section.subs.forEach((s) => {
      (s.items || []).forEach((i) => {
        const m = markFor(index, looseIndex, role, i);
        if (m && !m.edited) n++;
      });
      (s.rows || []).forEach((r) => {
        if (findProv(index, looseIndex, role, r.prov)) n++;
        r.fields.forEach((i) => {
          const m = markFor(index, looseIndex, role, i);
          if (m && !m.edited) n++;
        });
      });
      (s.tableRows || []).forEach((r) => {
        if (findProv(index, looseIndex, role, r.prov)) n++;
      });
    });
    return n;
  };

  const subHasContent = (s: SubSection) =>
    (s.items && s.items.length > 0) || (s.rows && s.rows.length > 0) || (s.tableRows && s.tableRows.length > 0) || s.kind === "declarations";

  const renderFields = (items: FieldItem[], role: Role) => (
    <div className="lop-fields">
      {items.map((i) => {
        const mark = markFor(index, looseIndex, role, i);
        const cls = ["lop-field", i.span ? `span-${i.span}` : "", mark ? (mark.edited ? "is-edited" : "is-extracted") : ""].filter(Boolean).join(" ");
        return (
          <div key={i.key} className={cls}>
            <div className="lop-field-label">{i.label}</div>
            <div className="lop-field-value">
              <span>{i.value}</span>
              <Tag mark={mark} t={t} onOpen={openDoc} />
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderRows = (rows: RowItem[], role: Role) => (
    <>
      {rows.map((r) => {
        const rowProv = findProv(index, looseIndex, role, r.prov);
        return (
          <div key={r.key} className={`lop-row-card${rowProv ? " is-extracted" : ""}`}>
            <div className="lop-row-card-head">
              <span>{r.title}</span>
              {rowProv ? <Tag mark={{ prov: rowProv, edited: false }} t={t} onOpen={openDoc} /> : null}
            </div>
            {renderFields(r.fields, role)}
          </div>
        );
      })}
    </>
  );

  const renderTable = (s: SubSection, role: Role) => {
    const rows = s.tableRows || [];
    const anyProv = rows.some((r) => findProv(index, looseIndex, role, r.prov));
    return (
      <table className="lop-table">
        <thead>
          <tr>
            {(s.columns || []).map((c, i) => (
              <th key={c} className={/value|balance|payment|income/i.test(c) && i > 0 ? "num" : ""}>
                {c}
              </th>
            ))}
            {anyProv ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const rowProv = findProv(index, looseIndex, role, r.prov);
            return (
              <tr key={r.key} className={rowProv ? "is-extracted" : ""}>
                {r.cells.map((c, i) => (
                  <td key={i} className={/^\$/.test(c) ? "num" : ""}>
                    {c}
                  </td>
                ))}
                {anyProv ? <td>{rowProv ? <Tag mark={{ prov: rowProv, edited: false }} t={t} onOpen={openDoc} /> : null}</td> : null}
              </tr>
            );
          })}
          {s.totalRow ? (
            <tr className="total">
              {s.totalRow.map((c, i) => (
                <td key={i} className={/^\$/.test(c) ? "num" : ""}>
                  {c}
                </td>
              ))}
              {anyProv ? <td /> : null}
            </tr>
          ) : null}
        </tbody>
      </table>
    );
  };

  const renderDeclarations = (items: FieldItem[]) => (
    <div className="lop-decl-list">
      {items.map((i) => (
        <div key={i.key} className="lop-decl-line">
          <span>{i.label}</span>
          <strong>{i.value}</strong>
        </div>
      ))}
    </div>
  );

  const renderSub = (s: SubSection, role: Role) => {
    if (!subHasContent(s)) return null;
    return (
      <div key={s.key} id={`lop-${s.key}`}>
        <div className="lop-sub-title">{s.title}</div>
        {s.kind === "fields" && s.items ? renderFields(s.items, role) : null}
        {s.kind === "rows" && s.rows ? renderRows(s.rows, role) : null}
        {s.kind === "table" ? renderTable(s, role) : null}
        {s.kind === "declarations" && s.items ? renderDeclarations(s.items) : null}
        {s.footer ? (
          <div style={{ textAlign: "right", marginTop: 10, fontSize: 13 }}>
            {s.footer.label} <strong>{s.footer.value}</strong>
          </div>
        ) : null}
      </div>
    );
  };

  const summary = () => {
    const s = status.summary || ({} as LOStatus["summary"]);
    const parts: string[] = [];
    const purpose = OPT.purpose[s.purpose as keyof typeof OPT.purpose] || s.purpose;
    if (purpose) parts.push(purpose);
    if (s.loan_amount) parts.push(formatMoney(s.loan_amount).replace(/\.00$/, ""));
    const addr = [s.address, [s.city, s.state].filter(Boolean).join(", ")].filter(Boolean).join(", ");
    if (addr) parts.push(addr);
    return parts.join(" · ");
  };

  const failedDocs = status.documents.filter((d) => d.failed);
  const totalFields = prov.length;
  const docsWithFields = new Set(prov.map((p) => p.doc_serialid)).size;
  const combinedIncome = parties.reduce((sum, p) => sum + monthlyIncomeOf(app, p.partyId), 0);

  const docsFor = (role: Role) => status.documents.filter((d) => (role === "coborrower" ? d.forwhom === "coborrower" : d.forwhom !== "coborrower"));

  // Labels for the preview drawer's "extracted from this document" list, from
  // every borrower's model so a co-borrower's document reads right too.
  const labelIndex = useMemo(() => {
    const m = new Map<string, { label: string; value: string }>();
    parties.forEach((party) => {
      buildReviewModel(app, party.partyId).forEach((section) => {
        const role: Role = section.shared ? "" : party.role;
        section.subs.forEach((s) => {
          const add = (i: FieldItem) => (i.prov || []).forEach((p) => m.set(provKey(role, loose(p)), { label: i.label, value: i.value }));
          (s.items || []).forEach(add);
          (s.rows || []).forEach((r) => {
            (r.prov || []).forEach((p) => m.set(provKey(role, loose(p)), { label: r.title, value: "" }));
            r.fields.forEach(add);
          });
          (s.tableRows || []).forEach((r) => (r.prov || []).forEach((p) => m.set(provKey(role, loose(p)), { label: r.cells.filter(Boolean).join(" · "), value: "" })));
        });
      });
    });
    return m;
  }, [app, parties]);

  const previewFields = preview ? prov.filter((p) => p.doc_serialid === preview.docId) : [];

  // The file arrives base64-encoded. It is shown through a blob: URL, not a
  // data: URL: the LOS's Content-Security-Policy allows blob: in frame-src
  // and not data:, so a data: iframe renders as "This content is blocked".
  const previewSrc = useMemo(() => {
    if (!previewData || previewData === "loading" || !previewData.content) return "";
    try {
      const bytes = Uint8Array.from(atob(previewData.content), (c) => c.charCodeAt(0));
      return URL.createObjectURL(new Blob([bytes], { type: previewData.mime || "application/octet-stream" }));
    } catch (e) {
      return "";
    }
  }, [previewData]);
  useEffect(() => () => { if (previewSrc) URL.revokeObjectURL(previewSrc); }, [previewSrc]);

  const previewBody = () => {
    if (previewData === "loading") return <div className="lop-note">{t("loadingPreview")}</div>;
    if (!previewData || !previewData.content || !previewSrc) return <div className="lop-note">{t("previewUnavailable")}</div>;
    const mime = previewData.mime || "";
    if (mime.startsWith("image/")) return <img src={previewSrc} alt={previewData.name} />;
    if (mime === "application/pdf" || /\.pdf$/i.test(previewData.name || "")) return <iframe title={previewData.name} src={previewSrc} />;
    return <div className="lop-note">{t("previewUnavailable")}</div>;
  };

  const confirmButton = (
    <button type="button" className="lop-btn-primary" disabled={busy || loading} onClick={onConfirm}>
      {t("confirmSync")}
      <ArrowRightOutlined rev={undefined} />
    </button>
  );

  return (
    <div className="lop-review-layout">
      <aside className="lop-rail">
        <div className="gop-panel">
          <div className="gop-eyebrow">{t("sections1003")}</div>
          <nav className="lop-rail-nav">
            {model.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => document.getElementById(`lop-${s.key}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                {SECTION_NAV[s.key]}
              </button>
            ))}
          </nav>
        </div>
        <div className="gop-panel">
          <div className="gop-eyebrow">{status.coborrower ? t("docsByPerson") : t("docsProcessed")}</div>
          <div className="lop-rail-hint">{t("clickToPreview")}</div>
          {status.documents.length === 0 ? <div className="lop-note">{t("noDocuments")}</div> : null}
          {parties.map((party) => {
            const docs = docsFor(party.role);
            if (!docs.length) return null;
            return (
              <div key={party.partyId}>
                {status.coborrower ? <div className="lop-rail-person">{party.name}</div> : null}
                {docs.map((d) => (
                  <button key={d.serialid} type="button" className="lop-rail-doc" onClick={() => openDoc(d.serialid)}>
                    {fileIcon(d.filename)}
                    <span className="lop-rail-doc-name">{d.filename}</span>
                    <span className={`lop-rail-doc-count${d.failed ? " is-warn" : ""}`}>
                      {d.failed ? t("couldntRead") : t("nFields", { n: d.fields })}
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </aside>

      <main>
        {failedDocs.length > 0 && !bannerDismissed ? (
          <div className="lop-warn-banner">
            <WarningOutlined rev={undefined} />
            <div style={{ flex: 1 }}>
              <strong>{failedDocs.length === 1 ? t("unreadableTitleOne") : t("unreadableTitle", { n: failedDocs.length })}</strong>
              <p>{t("unreadableNote")}</p>
              <div className="lop-warn-files">
                {failedDocs.map((d) => {
                  const owner = parties.find((p) => (d.forwhom === "coborrower" ? p.role === "coborrower" : p.role === "borrower"));
                  return (
                    <span key={d.serialid} className="lop-warn-file">
                      {fileIcon(d.filename)}
                      {d.filename}
                      {owner ? <small>{owner.name}</small> : null}
                    </span>
                  );
                })}
              </div>
            </div>
            <button type="button" className="lop-warn-dismiss" onClick={() => setBannerDismissed(true)}>
              {t("dismiss")}
            </button>
          </div>
        ) : null}

        <div className="lop-review-head">
          <div className="lop-review-head-top">
            <div className="lop-review-title">{t("reviewTitle")}</div>
            {confirmButton}
          </div>
          <div className="lop-review-lede">{t("reviewLede", { summary: summary() })}</div>
          {parties.length > 0 ? (
            <div className="lop-review-parties">
              <span>{t("urla")}</span>
              <div className="lop-party-tabs">
                {parties.map((p) => (
                  <button
                    key={p.partyId}
                    type="button"
                    className={`lop-party-tab${p.partyId === activeParty?.partyId ? " is-on" : ""}`}
                    onClick={() => setActivePartyId(p.partyId)}
                  >
                    {p.name}
                    <small>· {p.isPrimary ? t("roleBorrower") : t("roleCoborrower")}</small>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {combinedIncome > 0 ? (
            <div className="lop-review-income">
              {parties.length > 1 ? t("combinedIncome") : t("monthlyIncomeOf", { name: activeParty?.name || "" })}{" "}
              <strong>{formatMoney(parties.length > 1 ? combinedIncome : monthlyIncomeOf(app, activeParty?.partyId || ""))}</strong>
            </div>
          ) : null}
          <div className="lop-review-note">
            <span>
              <span className="lop-swatch" />
              {t("highlightNote")}
            </span>
            <strong>{t("fieldsFrom", { fields: totalFields, docs: docsWithFields })}</strong>
          </div>
        </div>

        {loading && !app ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : (
          model.map((section) => {
            const role = roleFor(section);
            const n = extractedCount(section);
            const subs = section.subs.filter(subHasContent);
            return (
              <section key={section.key} id={`lop-${section.key}`} className="lop-section">
                <div className="lop-section-head">
                  <span className="lop-section-code">{section.code}</span>
                  <div className="lop-section-title">
                    {section.title}
                    {section.shared && parties.length > 1 ? <small> · {t("shared")}</small> : null}
                  </div>
                  {n > 0 ? <span className="lop-pill">{t("fromDocs", { n })}</span> : null}
                  <button
                    type="button"
                    className="lop-btn-ghost"
                    onClick={() => setEditing({ anchor: section.subs[0]?.key || section.key, title: `${section.code} ${section.title}` })}
                  >
                    <EditOutlined rev={undefined} />
                    {t("edit")}
                  </button>
                </div>
                <div className="lop-section-body">
                  {subs.length === 0 ? <div className="lop-empty">{t("nothingYet")}</div> : subs.map((s) => renderSub(s, role))}
                </div>
              </section>
            );
          })
        )}

        <div className="lop-review-foot">
          <span>{parties.length > 1 ? t("checkBothThenSync") : t("checkThenSync")}</span>
          {confirmButton}
        </div>
      </main>

      <Drawer
        className="lop-edit-drawer"
        title={editing ? t("edit1003Title", { section: editing.title }) : ""}
        width={Math.min(1180, typeof window !== "undefined" ? window.innerWidth - 40 : 1180)}
        open={!!editing}
        destroyOnClose
        onClose={() => {
          setEditing(null);
          load();
        }}
        extra={
          <button
            type="button"
            className="lop-btn-primary lop-btn-small"
            onClick={() => {
              setEditing(null);
              load();
            }}
          >
            {t("doneEditing")}
          </button>
        }
      >
        {editing ? (
          <Interview1003
            loanId={loanId}
            partyId={activeParty?.partyId || ""}
            partyName={activeParty?.name || ""}
            targetHref={`#${editing.anchor}`}
            onSwitchToPrimaryParty={(primaryPartyId: string) => setActivePartyId(primaryPartyId)}
          />
        ) : null}
      </Drawer>

      <Drawer
        title={
          preview ? (
            <span>
              {preview.doc?.filename || previewFields[0]?.filename || ""}
              {preview.doc?.doc_title ? <span style={{ color: "#71717a", fontWeight: 400 }}> · {preview.doc.doc_title}</span> : null}
            </span>
          ) : (
            ""
          )
        }
        width={720}
        open={!!preview}
        destroyOnClose
        onClose={() => {
          setPreview(null);
          setPreviewData(null);
        }}
      >
        {preview ? (
          <>
            <div className="lop-preview-frame">{previewBody()}</div>
            <div className="lop-preview-fields">
              <div className="gop-eyebrow">{t("extractedFrom")}</div>
              {previewFields.length === 0 ? (
                <div className="lop-preview-row">
                  <span style={{ width: "100%" }}>{t("noFieldsFromDoc")}</span>
                </div>
              ) : (
                previewFields.map((p) => {
                  const known = labelIndex.get(provKey(p.role, loose(p.path)));
                  return (
                    <div key={`${p.role}-${p.path}`} className="lop-preview-row">
                      <span>{known?.label || p.path}</span>
                      <span>{known?.value || p.value}</span>
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : null}
      </Drawer>
    </div>
  );
};

export default LOReview1003;
